import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import AdminShell from "./AdminShell";
import { dialog } from "../Common/TreasuryDialog";

const API = process.env.REACT_APP_API_URL || "";

const FinanceManagement = () => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [holders, setHolders] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("distribute"); // distribute | history

  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  // Load all active properties
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/get_property`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.data || data.properties || []);
        // Show all active properties (status !== 4 = not liquidated)
        setProperties(list.filter((p) => p.status !== 4));
      })
      .catch(console.error);
  }, [token]);

  // Load holders when property selected
  const loadHolders = useCallback(
    (propId) => {
      if (!propId) return;
      setHoldersLoading(true);
      fetch(`${API}/api/finance/property-holders/${propId}`, { headers: authHeader })
        .then((r) => r.json())
        .then((data) => {
          setHolders(data.balances || []);
          setTotalTokens(data.totalTokens || 0);
        })
        .catch(console.error)
        .finally(() => setHoldersLoading(false));
    },
    [token]
  );

  useEffect(() => {
    if (selectedProperty) loadHolders(selectedProperty);
  }, [selectedProperty, loadHolders]);

  // Load history
  const loadHistory = useCallback(() => {
    fetch(`${API}/api/finance/dividend-history`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => setHistory(data.history || []))
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    if (activeTab === "history") loadHistory();
  }, [activeTab, loadHistory]);

  const perToken =
    amount && totalTokens > 0
      ? (parseFloat(amount) / totalTokens).toFixed(4)
      : "—";

  const selectedProp = properties.find((p) => p._id === selectedProperty);

  const handleDistribute = async () => {
    if (!selectedProperty || !amount || parseFloat(amount) <= 0) {
      dialog.error("Validation Error", "Please select a property and enter a valid amount.");
      return;
    }
    const confirmed = await dialog.confirm(
      "Confirm Dividend Distribution",
      `Distribute SAR ${parseFloat(amount).toLocaleString()} to ${holders.length} holders of "${selectedProp?.title}"?\n\nPer-token amount: SAR ${perToken}`
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/finance/distribute-dividends`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId: selectedProperty, totalAmount: parseFloat(amount), note }),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Success", data.msg);
        setAmount("");
        setNote("");
        loadHolders(selectedProperty);
      } else {
        dialog.error("Error", data.msg || "Distribution failed.");
      }
    } catch (err) {
      dialog.error("Error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLiquidate = async () => {
    if (!selectedProperty || !amount || parseFloat(amount) <= 0) {
      dialog.error("Validation Error", "Please select a property and enter the liquidation amount.");
      return;
    }
    const confirmed = await dialog.confirm(
      "⚠️ Confirm Property Liquidation",
      `This will PERMANENTLY liquidate "${selectedProp?.title}".\n\nTotal payout: SAR ${parseFloat(amount).toLocaleString()}\nPer-token: SAR ${perToken}\nRecipients: ${holders.length}\n\nAll token balances will be zeroed. This cannot be undone.`
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/finance/liquidate-property`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId: selectedProperty, totalAmount: parseFloat(amount), note }),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Liquidation Complete", data.msg);
        setAmount("");
        setNote("");
        setSelectedProperty("");
        setHolders([]);
        setTotalTokens(0);
        // Refresh properties list
        const pRes = await fetch(`${API}/api/property/all`, { headers: authHeader });
        const pData = await pRes.json();
        const deployed = (pData.properties || pData || []).filter(
          (p) => p.contract_address && p.status !== 4
        );
        setProperties(deployed);
      } else {
        dialog.error("Error", data.msg || "Liquidation failed.");
      }
    } catch (err) {
      dialog.error("Error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (type) => {
    const styles = {
      dividend: { background: "#dcfce7", color: "#166534" },
      liquidation: { background: "#fee2e2", color: "#991b1b" },
    };
    return (
      <span
        style={{
          ...styles[type],
          padding: "2px 10px",
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "capitalize",
        }}
      >
        {type}
      </span>
    );
  };

  return (
    <AdminShell pageTitle="Finance Management">
      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: "2px solid #e5e7eb" }}>
        {["distribute", "history"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "none",
              border: "none",
              padding: "10px 24px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              color: activeTab === tab ? "#c9a84c" : "#6b7280",
              borderBottom: activeTab === tab ? "2px solid #c9a84c" : "2px solid transparent",
              marginBottom: -2,
              transition: "all 0.18s",
              textTransform: "capitalize",
            }}
          >
            {tab === "distribute" ? "Distribute / Liquidate" : "Distribution History"}
          </button>
        ))}
      </div>

      {activeTab === "distribute" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Left: Form */}
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
            <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#111827" }}>
              Dividend / Liquidation
            </h3>

            {/* Property selector */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Property *
              </label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 14,
                  color: "#111827",
                  background: "#fff",
                  outline: "none",
                }}
              >
                <option value="">— Select a deployed property —</option>
                {properties.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.title} ({p.city})
                  </option>
                ))}
              </select>
            </div>

            {/* Amount */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Total Amount (SAR) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 50000"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 14,
                  color: "#111827",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Note */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Note (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Q2 2026 rental income distribution"
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 14,
                  color: "#111827",
                  outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Preview box */}
            {selectedProperty && amount && (
              <div
                style={{
                  background: "#fefce8",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 20,
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Distribution Preview</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, color: "#78350f" }}>
                  <span>Total Amount:</span>
                  <strong>SAR {parseFloat(amount || 0).toLocaleString()}</strong>
                  <span>Total Tokens:</span>
                  <strong>{totalTokens.toLocaleString()}</strong>
                  <span>Per Token:</span>
                  <strong>SAR {perToken}</strong>
                  <span>Recipients:</span>
                  <strong>{holdersLoading ? "Loading…" : holders.length}</strong>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={handleDistribute}
                disabled={loading || !selectedProperty || !amount}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  background: loading ? "#e5e7eb" : "linear-gradient(135deg,#c9a84c,#7a5c10)",
                  color: loading ? "#9ca3af" : "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "opacity 0.18s",
                }}
              >
                {loading ? "Processing…" : "Distribute Dividends"}
              </button>
              <button
                onClick={handleLiquidate}
                disabled={loading || !selectedProperty || !amount}
                style={{
                  flex: 1,
                  padding: "11px 0",
                  background: loading ? "#e5e7eb" : "#dc2626",
                  color: loading ? "#9ca3af" : "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "opacity 0.18s",
                }}
              >
                {loading ? "Processing…" : "Liquidate Property"}
              </button>
            </div>
          </div>

          {/* Right: Token holders */}
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
            <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#111827" }}>
              Token Holders
              {selectedProperty && (
                <span style={{ fontSize: 12, fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>
                  {holdersLoading ? "Loading…" : `${holders.length} holders · ${totalTokens.toLocaleString()} tokens`}
                </span>
              )}
            </h3>

            {!selectedProperty ? (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
                Select a property to view token holders
              </div>
            ) : holdersLoading ? (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
                Loading holders…
              </div>
            ) : holders.length === 0 ? (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
                No token holders found for this property
              </div>
            ) : (
              <div style={{ overflowY: "auto", maxHeight: 420 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                      <th style={{ textAlign: "left", padding: "8px 10px", color: "#6b7280", fontWeight: 600 }}>Holder</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: "#6b7280", fontWeight: 600 }}>Tokens</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: "#6b7280", fontWeight: 600 }}>Share %</th>
                      {amount && (
                        <th style={{ textAlign: "right", padding: "8px 10px", color: "#6b7280", fontWeight: 600 }}>Payout</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {holders.map((h, i) => {
                      const share = totalTokens > 0 ? ((h.units / totalTokens) * 100).toFixed(2) : 0;
                      const payout = amount ? (h.units * parseFloat(perToken)).toFixed(2) : null;
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "10px 10px", color: "#111827" }}>
                            <div style={{ fontWeight: 600 }}>{h.userId?.name || "—"}</div>
                            <div style={{ fontSize: 11, color: "#9ca3af" }}>{h.userId?.email || ""}</div>
                          </td>
                          <td style={{ padding: "10px 10px", textAlign: "right", fontWeight: 600, color: "#c9a84c" }}>
                            {h.units.toLocaleString()}
                          </td>
                          <td style={{ padding: "10px 10px", textAlign: "right", color: "#374151" }}>{share}%</td>
                          {amount && (
                            <td style={{ padding: "10px 10px", textAlign: "right", fontWeight: 600, color: "#166534" }}>
                              SAR {payout}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "history" && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
          <h3 style={{ margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#111827" }}>
            Distribution History
          </h3>
          {history.length === 0 ? (
            <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
              No distributions recorded yet
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Property</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Total (SAR)</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Per Token</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Recipients</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 12px", color: "#374151" }}>
                      {new Date(h.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
                    </td>
                    <td style={{ padding: "12px 12px", color: "#111827", fontWeight: 600 }}>
                      {h.propertyId?.title || "—"}
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{h.propertyId?.city}</div>
                    </td>
                    <td style={{ padding: "12px 12px" }}>{statusBadge(h.type)}</td>
                    <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700, color: "#c9a84c" }}>
                      {h.totalAmount.toLocaleString()}
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", color: "#374151" }}>
                      {h.perTokenAmount.toFixed(4)}
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", color: "#374151" }}>
                      {h.recipientCount}
                    </td>
                    <td style={{ padding: "12px 12px", color: "#6b7280", fontSize: 12 }}>
                      {h.note || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </AdminShell>
  );
};

export default FinanceManagement;
